import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang, IMAGES } from "../i18n";
import { useLenis } from "../hooks/useLenis";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { LeadForm } from "../components/LeadForm";
import { Reveal, SectionLabel, MaskLine } from "../components/Motion";
import { Seo } from "../components/Seo";
import {
  ArrowLeft, ArrowUpRight, ArrowDown, ScanSearch, Sprout, FlaskConical,
  Shuffle, Leaf, Cpu, Check, CircleDot, Circle, Handshake, Info,
} from "lucide-react";

const POT_ICONS = [ScanSearch, Sprout, FlaskConical, Shuffle, Leaf, Cpu];

const STATUS_STYLE = {
  done: { icon: Check, cls: "bg-[var(--green)] text-white border-[var(--green)]", textCls: "text-[var(--green)]" },
  current: { icon: CircleDot, cls: "bg-[var(--gold)]/15 text-[var(--gold)] border-[var(--gold)]/60", textCls: "text-[var(--gold)]" },
  next: { icon: Circle, cls: "bg-transparent text-[var(--text-secondary)] border-[var(--border)]", textCls: "text-[var(--text-secondary)]" },
};

export default function OreganoScan() {
  useLenis();
  const { t, lang } = useLang();
  const o = t.oregano;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lang]);

  const scrollToForm = () =>
    document.getElementById("richiedi-info")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="grain relative">
      <Seo page="oregano" />
      <Nav />
      <main>
        {/* HERO */}
        <section data-testid="oregano-hero" className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden">
          <div className="absolute -top-24 right-0 w-[560px] h-[560px] rounded-full bg-[#83B94A]/15 blur-[160px] pointer-events-none" />
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7">
              <Link to="/" data-testid="oregano-back" className="inline-flex items-center gap-2 font-jetbrains text-[11px] tracking-[0.2em] uppercase text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors mb-10">
                <ArrowLeft className="w-4 h-4" /> Amazonico3000
              </Link>
              <SectionLabel>{o.label}</SectionLabel>
              <h1 className="font-cormorant font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--text-primary)] uppercase">
                <MaskLine delay={0.1}>{o.title}</MaskLine>
              </h1>
              <Reveal delay={0.25}>
                <p className="font-cormorant italic text-2xl md:text-3xl text-[var(--text-secondary)] mt-6 max-w-xl">{o.subtitle}</p>
                <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6 max-w-2xl">{o.intro}</p>
                <p className="font-jetbrains text-[11px] tracking-wider leading-relaxed text-[var(--gold)] mt-6 max-w-2xl uppercase">{o.fullTitle}</p>
                <a href="#visione" data-testid="oregano-cta" className="inline-flex items-center gap-3 mt-10 rounded-full bg-[#103F31] text-[#F2F0E8] px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase hover:bg-[#18252A] transition-colors duration-300">
                  {o.cta} <ArrowDown className="w-4 h-4" />
                </a>
              </Reveal>
            </div>
            <Reveal delay={0.25} className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-[2rem] border border-[var(--border)] group">
                <img src={IMAGES.oreganoScan} alt="Oregano Scan — dispositivo esterno chiuso installato su pianta con supporto metallico indipendente" data-testid="oregano-hero-img" className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105" />
                <span className="absolute top-6 left-6 w-6 h-6 border-t border-l border-[#83B94A]/70" />
                <span className="absolute bottom-6 right-6 w-6 h-6 border-b border-r border-[#83B94A]/70" />
              </div>
            </Reveal>
          </div>
        </section>

        {/* MANIFESTO */}
        <section className="py-14 md:py-20 bg-[#103F31] text-[#F2F0E8]">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <Reveal>
              <p className="font-cormorant italic font-light text-2xl md:text-3xl leading-snug">{o.manifesto}</p>
            </Reveal>
          </div>
        </section>

        {/* VISION */}
        <section id="visione" data-testid="oregano-vision" className="py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <SectionLabel>{o.visionLabel}</SectionLabel>
            <div className="grid lg:grid-cols-12 gap-10 mt-6">
              <h2 className="lg:col-span-6 font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)]">{o.visionTitle}</h2>
              <p className="lg:col-span-6 font-outfit font-light text-[var(--text-secondary)] leading-relaxed">{o.visionText}</p>
            </div>
          </div>
        </section>

        {/* DEVICE */}
        <section data-testid="oregano-device" className="py-16 md:py-24 bg-[var(--bg-secondary)] border-y border-[var(--border)]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
            <Reveal className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-[2rem] border border-[var(--border)]">
                <img src={IMAGES.oreganoScan} alt="Oregano Scan — vista esterna del dispositivo chiuso con supporto indipendente" className="w-full h-full object-cover" />
              </div>
            </Reveal>
            <div className="lg:col-span-7">
              <SectionLabel>{o.deviceLabel}</SectionLabel>
              <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)]">{o.deviceTitle}</h2>
              <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6">{o.deviceText}</p>
              <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-4">{o.deviceText2}</p>
              <ul className="grid sm:grid-cols-2 gap-x-8 mt-8">
                {o.devicePoints.map((pt, i) => (
                  <li key={i} data-testid={`oregano-device-point-${i + 1}`} className="flex items-center gap-3 border-b border-[var(--border)] py-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#83B94A] shrink-0" />
                    <span className="font-outfit text-[var(--text-primary)]">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* POTENTIAL */}
        <section data-testid="oregano-potential" className="py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <SectionLabel>{o.potLabel}</SectionLabel>
            <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)] max-w-2xl">{o.potTitle}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
              {o.pots.map((card, i) => {
                const Icon = POT_ICONS[i] || Leaf;
                return (
                  <Reveal key={i} delay={i * 0.06}>
                    <div data-testid={`oregano-pot-${i + 1}`} className="group h-full rounded-2xl border border-[var(--border)] p-7 hover:border-[#83B94A]/60 transition-colors duration-500">
                      <span className="w-11 h-11 rounded-full bg-[#103F31]/10 flex items-center justify-center text-[#103F31] group-hover:bg-[#103F31] group-hover:text-[#F2F0E8] transition-colors duration-500">
                        <Icon className="w-5 h-5" />
                      </span>
                      <h3 className="font-cormorant text-2xl text-[var(--text-primary)] mt-5">{card.t}</h3>
                      <p className="font-outfit font-light text-sm text-[var(--text-secondary)] leading-relaxed mt-2">{card.d}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <p className="font-jetbrains text-[11px] tracking-wider text-[var(--text-secondary)] mt-8 uppercase">{o.potDisclaimer}</p>
          </div>
        </section>

        {/* ORIGIN */}
        <section data-testid="oregano-origin" className="py-16 md:py-24 bg-[#103F31] text-[#F2F0E8]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <SectionLabel>{o.originLabel}</SectionLabel>
            <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight max-w-2xl mt-6">{o.originTitle}</h2>
            <p className="font-outfit font-light text-[#F2F0E8]/80 leading-relaxed mt-6 max-w-3xl">{o.originText}</p>
            <Link to="/tenute" data-testid="oregano-tenute-cta" className="inline-flex items-center gap-3 mt-10 rounded-full border border-[#83B94A] px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase text-[#83B94A] hover:bg-[#83B94A] hover:text-[#103F31] transition-colors duration-300">
              {o.originCta} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* R&D TIMELINE */}
        <section data-testid="oregano-rd" className="py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <SectionLabel>{o.rdLabel}</SectionLabel>
            <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)] mt-6">{o.rdTitle}</h2>
            <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6 max-w-3xl">{o.rdText}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
              {o.rdSteps.map((step, i) => {
                const st = STATUS_STYLE[step.status];
                const Icon = st.icon;
                const statusLabel = step.status === "done" ? o.rdDone : step.status === "current" ? o.rdCurrent : o.rdNext;
                return (
                  <Reveal key={i} delay={i * 0.06}>
                    <div data-testid={`oregano-rd-${i + 1}`} className="h-full rounded-2xl border border-[var(--border)] p-6">
                      <div className="flex items-center justify-between">
                        <span className="font-jetbrains text-[11px] text-[var(--text-secondary)]">{String(i + 1).padStart(2, "0")}</span>
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 ${st.cls}`}>
                          <Icon className="w-3 h-3" />
                          <span className="font-jetbrains text-[9px] tracking-widest uppercase">{statusLabel}</span>
                        </span>
                      </div>
                      <h3 className={`font-cormorant text-2xl mt-4 ${step.status === "next" ? "text-[var(--text-secondary)]" : "text-[var(--text-primary)]"}`}>{step.t}</h3>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <p className="font-jetbrains text-[11px] tracking-wider text-[var(--text-secondary)] mt-8 uppercase max-w-3xl">{o.rdNote}</p>
          </div>
        </section>

        {/* SUSTAINABILITY */}
        <section data-testid="oregano-sust" className="py-16 md:py-24 bg-[var(--bg-secondary)] border-y border-[var(--border)]">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
            <SectionLabel>{o.sustLabel}</SectionLabel>
            <Reveal>
              <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)] mt-6">{o.sustTitle}</h2>
              <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6 max-w-3xl mx-auto">{o.sustText}</p>
            </Reveal>
          </div>
        </section>

        {/* PARTNERSHIP */}
        <section data-testid="oregano-partner" className="py-16 md:py-28">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
            <SectionLabel>{o.partnerLabel}</SectionLabel>
            <Reveal>
              <h2 className="font-cormorant font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[var(--text-primary)] mt-6">{o.partnerTitle}</h2>
              <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6 max-w-2xl mx-auto">{o.partnerText}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-wrap justify-center gap-3 mt-10">
                {o.partnerTargets.map((pt) => (
                  <span key={pt} className="font-jetbrains text-[11px] tracking-[0.15em] uppercase border border-[var(--border)] rounded-full px-5 py-2.5 text-[var(--text-secondary)]">{pt}</span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-12">
                <button onClick={scrollToForm} data-testid="oregano-partner-cta" className="inline-flex items-center gap-3 rounded-full bg-[#103F31] text-[#F2F0E8] px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase hover:bg-[#18252A] transition-colors duration-300">
                  <Handshake className="w-4 h-4" /> {o.partnerCta}
                </button>
                <button onClick={scrollToForm} data-testid="oregano-info-cta" className="inline-flex items-center gap-3 rounded-full border border-[var(--border)] px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase text-[var(--text-secondary)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors duration-300">
                  <Info className="w-4 h-4" /> {o.infoCta}
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        <LeadForm source="oregano-scan" interests={o.interests} label={o.label} />
      </main>
      <Footer />
    </div>
  );
}
